import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

import {
    setChonkyDefaults,
    ChonkyActions,
    FileHelper,
    FileBrowser, 
    FileNavbar,
    FileToolbar,
    FileList,
    defineFileAction,
    ChonkyIconName,
} from 'chonky';
import { ChonkyIconFA } from 'chonky-icon-fontawesome';

import { broncoURL, commitHash, vultureENV } from 'Vulture/ENV';
import { getSession } from 'Vulture/Session';

import CustomIcon from '../Common/CustomIcon/CustomIcon';
import CreateFolderModal from './CreateFolderModal.js';
import CreateFileModal from './CreateFileModal.js';
import EditFileModal from './EditFileModal.js';
import EditFolderModal from './EditFolderModal.js';
import { transformFolderData, transformFileData, transformSearchFileData, transformSearchFolderData } from '../../../Util/ChonkyFileBrowserETL';

import style from './ChonkyFileBrowser.module.css';

setChonkyDefaults({ 
    iconComponent: ChonkyIconFA,
});

const initialFileMap = {
    root: {
        id: 'root',
        name: 'Home',
        isDir: true,
        childrenIds: [],
        parentId: null,
    },
};

function ChonkyFileBrowser(props) {
    const [selectedFileIds, setSelectedFileIds] = useState(new Set());
    const [fileMap, setFileMap] = useState(initialFileMap);
    const [isListView, setIsListView] = useState(true);
    const [currentFolderId, setCurrentFolderId] = useState('root');
    const fileBrowserRef = useRef(null);
    const wrapperRef = useRef(null);
    const fileMapRef = useRef(fileMap);
    const fileIconErrorMap = useRef({});

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const [sortColumn, setSortColumn] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');

    const files = useMemo(() => {
        if (isSearching) {
            let folderResults = []
            let fileResults = []
            let completeResults = [];

            if(searchResults?.folders) {
                folderResults = transformSearchFolderData(searchResults?.folders);
            }
            if(searchResults?.files) {
                fileResults = transformSearchFileData(searchResults?.files);
            }

            completeResults = [...folderResults, ...fileResults];
            
            return completeResults;
        } else {
            const currentFolder = fileMap[currentFolderId];
            if (!currentFolder) return [];
            
            return currentFolder.childrenIds.map(id => fileMap[id]).filter(Boolean);
        }
    }, [currentFolderId, fileMap, isSearching, searchResults]);

    const filesSortedByNames = useMemo(() => {
        const sorted = [...files];
        sorted.sort((a, b) => {
            let aVal = a[sortColumn] || '';
            let bVal = b[sortColumn] || '';
            if (sortColumn === 'size') {
                aVal = a.size || 0;
                bVal = b.size || 0;
            }
            if (sortColumn === 'resolution') {
                aVal = a.resolution || '';
                bVal = b.resolution || '';
            }
            if (sortColumn === 'description') {
                aVal = a.description || '';
                bVal = b.description || '';
            }
            if (a.isDir !== b.isDir) return a.isDir ? -1 : 1; // Always sort folders first
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [files, sortColumn, sortDirection]);

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(dir => dir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const renderSortArrow = (column) => {
        if (sortColumn !== column) return <span style={{marginLeft: 4}}>⇅</span>;
        return (
            <span style={{marginLeft: 4}}>
                {sortDirection === 'asc' ? '▲' : '▼'}
            </span>
        );
    };

    useEffect(() => {
        fileMapRef.current = fileMap;
    }, [fileMap]);

    useEffect(() => {
        setIsListView(false);
    }, []);

    useEffect(() => {
        if (fileBrowserRef.current) {
            setSelectedFileIds(new Set(fileBrowserRef.current.getFileSelection()));
        }
    }, [filesSortedByNames]);

    const getAccountsData = useCallback(() => {
        props.app.setState({ loading: true }); 
        const session = getSession();
        const url = `${broncoURL}/api/folder`;
        
        const headers = {
            headers: {
                'X-Auth-Session': session.session,
                'X-Spark-API-version': commitHash,
            },
        };
    
        axios.get(url, headers)
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error(`Invalid response from ${broncoURL}/api/folder`);
                }
                return res.data;
            })
            .then((folderData) => {
                setFileMap(transformFolderData(folderData));
                props.app.setState({ loading: false });
            })
            .catch((err) => {
                console.error('Folder fetch error:', {
                    error: err.message,
                    response: err.response?.data
                });
                alert(`Failed to load folders: ${err.response?.data?.message || err.message}`);
                setFileMap(initialFileMap); 
                props.app.setState({ loading: false });
            });
    }, []);

    const openCreateFolderModal = () => {
        props.app.openModal({
            modalSize:  1,
            showModal:  true,
            modalProps: {
                title: 'Add New Folder',
                jsx:   (
                    <CreateFolderModal 
                        currentFolderId={currentFolderId}
                        refreshFileBrowserData={refreshFileBrowserData}
                        app={props.app}
                    />
                ),
            },
        });
    }

    const openCreateFileModal = () => {
        props.app.openModal({
            modalSize:  1,
            showModal:  true,
            modalProps: {
                title: 'Upload New File',
                jsx:   (
                    <CreateFileModal 
                        currentFolderId={currentFolderId}
                        refreshFileBrowserData={refreshFileBrowserData}
                        app={props.app}
                    />
                ),
            },
        });
    }

    const handleDownloadFile = async () => {
        const selectedFileIds = Array.from(fileBrowserRef.current.getFileSelection());
        const selectedFiles = selectedFileIds.map(id => fileMapRef.current[id]).filter(Boolean);
        
        if (selectedFiles.length === 0) {
            alert('Please select at least one file to download');
            return;
        }
        
        const filesToDownload = selectedFiles.filter(file => !file.isDir);
        
        if (filesToDownload.length === 0) {
            alert('Please select at least one file to download (folders cannot be downloaded)');
            return;
        }
        
        if (filesToDownload.length > 1) {
            const confirmed = window.confirm(`Are you sure you want to download ${filesToDownload.length} files?`);
            if (!confirmed) {
                return;
            }
        }
        
        props.app.setState({ loading: true });
        
        try {
            for (const file of filesToDownload) {
                const fileUrl = `https://ts-condor-assets.s3.us-east-1.amazonaws.com/${vultureENV}/ts-library/${file.path}`;
                const fileName = file.name || 'downloaded_file';
                
                await downloadFromUrl(fileUrl, fileName);
            }
        } catch (error) {
            console.error('Download failed:', error);
            alert(`Failed to download: ${error.message}`);
        } finally {
            props.app.setState({ loading: false });
        }
    }

    const downloadFromUrl = async (url, filename) => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(blob);
            downloadLink.download = filename;
            
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(downloadLink.href);
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download the file. Please try again later.');
        }
    };

    const openEditModal = () => {
        const selectedFileIds = Array.from(fileBrowserRef.current.getFileSelection());
        const selectedFiles = selectedFileIds.map(id => fileMapRef.current[id]).filter(Boolean);
    
        if (selectedFiles.length !== 1) {
            alert('Please select exactly one item to edit');
            return;
        }
    
        const selectedItem = selectedFiles[0];
    
        if (selectedItem.id.startsWith('file_')) {
            props.app.openModal({
                modalSize: 1,
                showModal: true,
                modalProps: {
                    title: 'Edit File',
                    jsx: (
                        <EditFileModal
                            file={selectedItem}
                            refreshFileBrowserData={refreshFileBrowserData}
                            app={props.app}
                        />
                    ),
                },
            });
        } else if (selectedItem.id.startsWith('node_')) {
            props.app.openModal({
                modalSize: 1,
                showModal: true,
                modalProps: {
                    title: 'Edit Folder',
                    jsx: (
                        <EditFolderModal
                            folder={selectedItem}
                            refreshFileBrowserData={refreshFileBrowserData}
                            app={props.app}
                        />
                    ),
                },
            });
        }
    };
    
    useEffect(() => {
        if (currentFolderId === 'root') return;
    
        const folder = fileMapRef.current[currentFolderId];
        if (!folder) return;
    
        const hasFiles = folder.childrenIds.some(id => id.startsWith('file_'));
        if (hasFiles) return;
    
        props.app.setState({ loading: true });
        const backendFolderId = currentFolderId.replace('node_', '');
        const session = getSession();
        const url = `${broncoURL}/api/folder/${backendFolderId}/file`;
        const headers = {
            headers: {
                'X-Auth-Session': session.session,
                'X-Spark-API-version': commitHash,
            },
        };
    
        axios.get(url, headers)
        .then(res => {
            props.app.setState({ loading: false });
            if (res.status !== 200) throw new Error(`Invalid response from ${url}`);
            return res.data.files; 
        })
        .then(filesArray => {
            setFileMap(prevMap => 
                transformFileData(prevMap, currentFolderId, filesArray)
            );
        })
        .catch(err => {
            console.error('File fetch error:', err);
            alert(`Failed to load files: ${err.response?.data?.message || err.message}`);
            props.app.setState({ loading: false });
        });
    }, [currentFolderId]);

    useEffect(() => {
        getAccountsData();
    }, [getAccountsData]);

    const refreshFileBrowserData = useCallback(async () => {
        props.app.setState({ loading: true }); 
        try {
            const session = getSession();
            const folderUrl = `${broncoURL}/api/folder`;
            const headers = {
                headers: {
                    'X-Auth-Session': session.session,
                    'X-Spark-API-version': commitHash,
                },
            };
    
            const folderResponse = await axios.get(folderUrl, headers);
            if (folderResponse.status !== 200) {
                throw new Error(`Invalid response from ${folderUrl}`);
            }
            
            const newFileMap = transformFolderData(folderResponse.data);
            
            if (currentFolderId !== 'root') {
                const backendFolderId = currentFolderId.replace('node_', '');
                const filesUrl = `${broncoURL}/api/folder/${backendFolderId}/file`;
                const filesResponse = await axios.get(filesUrl, headers);
                
                if (filesResponse.status === 200) {
                    setFileMap(transformFileData(newFileMap, currentFolderId, filesResponse.data.files));
                    return;
                }
            }
            
            setFileMap(newFileMap);
        } catch (err) {
            console.error('Refresh error:', err);
            alert(`Failed to refresh data: ${err.response?.data?.message || err.message}`);
        } finally {
            props.app.setState({ loading: false });
        }
    }, [currentFolderId]);
  
    const performRecursiveSearch = async (query) => {
        try {
            props.app.setState({ loading: true });
            setIsSearching(true);
            
            const session = getSession();
            const url = `${broncoURL}/api/search?query=${query}`;
            
            const headers = {
                headers: {
                    'X-Auth-Session': session.session,
                    'X-Spark-API-version': commitHash,
                },
            };
        
            const res = await axios.get(url, headers);
            
            if (res.status !== 200) {
                throw new Error(`Invalid response from ${broncoURL}/api/search`);
            }
            
            
            setSearchResults(res.data);
        } catch (err) {
            console.error("Search failed:", err);
        } finally {
            props.app.setState({ loading: false });
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if(searchQuery.trim().length > 2) {
                performRecursiveSearch(searchQuery);
            } else {
                setIsSearching(false);
            }
        }, 300);
        
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const folderChain = useMemo(() => {
        const chain = [];
        let currentId = currentFolderId;

        while (currentId) {
            const folder = fileMap[currentId];
            chain.unshift(folder);
            currentId = folder.parentId;
        }

        return chain;
    }, [currentFolderId, fileMap]);

    const renderFileIcon = (file, size = 28) => {
        const imgError = fileIconErrorMap.current[file.id] || false;
        if (file.isDir) return '📁';
        if (imgError) return '📄';
        return (
            <img
                src={`https://ts-condor-assets.s3.us-east-1.amazonaws.com/${vultureENV}/ts-library/${file.path}`}
                alt="file"
                style={{ width: size, height: 'auto', objectFit: 'contain', verticalAlign: 'middle' }}
                onError={() => {
                    fileIconErrorMap.current[file.id] = true;
                    setFileMap(fm => ({ ...fm }));
                }}
            />
        );
    };

    const CustomFileList = ({ files, onFileSelected, selectedFileIds }) => {
        const handleCheckboxChange = (file, checked) => {
            const selection = new Set(selectedFileIds);
            if (checked) {
                selection.add(file.id);
            } else {
                selection.delete(file.id);
            }
            fileBrowserRef.current.setFileSelection(selection);
            setSelectedFileIds(new Set(selection)); 
        };

        if (isListView) {
            return (
                <table className={style.customFileListTable}>
                    <thead className={style.customFileListThead}>
                        <tr>
                            <th className={`${style.customFileListTh} ${style.icon}`}></th>
                            <th
                                className={style.customFileListTh}
                                onClick={() => handleSort('name')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                Name {renderSortArrow('name')}
                            </th>
                            <th
                                className={style.customFileListTh}
                                onClick={() => handleSort('size')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                Size {renderSortArrow('size')}
                            </th>
                            <th
                                className={style.customFileListTh}
                                onClick={() => handleSort('resolution')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                Resolution {renderSortArrow('resolution')}
                            </th>
                            <th
                                className={style.customFileListTh}
                                onClick={() => handleSort('description')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                Description {renderSortArrow('description')}
                            </th>
                            <th className={style.customFileListTh}></th> 
                        </tr>
                    </thead>
                    <tbody className={style.customFileListTbody}>
                        {files.map((file) => {
                            const FileIcon = () => renderFileIcon(file, 28);
                            const isSelected = selectedFileIds.has(file.id);
                            return (
                                <tr
                                    key={file.id}
                                    className={`${style.customFileListTr}${isSelected ? ' ' + style.selected : ''}`}
                                    onClick={e => {
                                        if (file.isDir && e.target.type !== 'checkbox') {
                                            setCurrentFolderId(file.id);
                                        } else if (!file.isDir && e.target.type !== 'checkbox') {
                                            handleCheckboxChange(file, !isSelected);
                                        }
                                    }}
                                >
                                    <td className={`${style.customFileListTd} ${style.icon}`}>
                                        <span>
                                            <FileIcon />
                                        </span>
                                    </td>
                                    <td className={style.customFileListTd}>
                                        {file.name}
                                    </td>
                                    <td className={`${style.customFileListTd} ${style.size}`}>
                                        {formatBytes(file.size)}
                                    </td>
                                    <td className={`${style.customFileListTd} ${style.resolution}`}>
                                        {file.resolution || '-'}
                                    </td>
                                    <td className={`${style.customFileListTd} ${style.description}`}>
                                        {file.description || '-'}
                                    </td>
                                    <td className={`${style.customFileListTd} ${style.lastFileListTd}` }>
                                        <div className={style.actionBtnRow}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={e => handleCheckboxChange(file, e.target.checked)}
                                                onClick={e => e.stopPropagation()}
                                                className={style.checkboxLarge}
                                            />
                                            <button
                                                className={style.customGridActionBtn}
                                                title="Edit"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setSelectedFileIds(new Set([file.id]));
                                                    fileBrowserRef.current.setFileSelection(new Set([file.id]));
                                                    openEditModal();
                                                }}
                                            >
                                                <CustomIcon icon="edit" />
                                            </button>
                                            {!file.isDir && (
                                                <button
                                                    className={style.customGridActionBtn}
                                                    title="Download"
                                                    onClick={async e => {
                                                        e.stopPropagation();
                                                        setSelectedFileIds(new Set([file.id]));
                                                        fileBrowserRef.current.setFileSelection(new Set([file.id]));
                                                        await handleDownloadFile();
                                                    }}
                                                >
                                                    <CustomIcon icon="download" />
                                                </button>
                                            )}
                                            <button
                                                className={style.customGridActionBtn}
                                                title="Delete"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setSelectedFileIds(new Set([file.id]));
                                                    fileBrowserRef.current.setFileSelection(new Set([file.id]));
                                                    handleFileAction({ id: ChonkyActions.DeleteFiles.id });
                                                }}
                                            >
                                                <CustomIcon icon="trash" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        } else {
            return (
                <div className={style.customGridView}>
                    {files.map((file) => {
                        const FileIcon = () => renderFileIcon(file, 96);
                        const isSelected = selectedFileIds.has(file.id);

                        const handleEdit = (e) => {
                            e.stopPropagation();
                            setSelectedFileIds(new Set([file.id]));
                            fileBrowserRef.current.setFileSelection(new Set([file.id]));
                            openEditModal();
                        };
                        const handleDownload = async (e) => {
                            e.stopPropagation();
                            setSelectedFileIds(new Set([file.id]));
                            fileBrowserRef.current.setFileSelection(new Set([file.id]));
                            await handleDownloadFile();
                        };
                        const handleDelete = (e) => {
                            e.stopPropagation();
                            setSelectedFileIds(new Set([file.id]));
                            fileBrowserRef.current.setFileSelection(new Set([file.id]));
                            handleFileAction({ id: ChonkyActions.DeleteFiles.id });
                        };

                        return (
                            <div
                                key={file.id}
                                className={`${style.customGridItem} ${isSelected ? style.selected : ''}`}
                                onClick={e => {
                                    if (file.isDir && e.target.type !== 'checkbox') {
                                        setCurrentFolderId(file.id);
                                    } else if (!file.isDir && e.target.type !== 'checkbox') {
                                        handleCheckboxChange(file, !isSelected);
                                    }
                                }}
                                title={
                                    `Name: ${file.name}\n` +
                                    `Size: ${formatBytes(file.size)}\n` +
                                    `Resolution: ${file.resolution || '-'}\n` +
                                    `Description: ${file.description || '-'}`
                                }
                                style={{ cursor: file.isDir ? 'pointer' : 'default' }}
                            >
                                <div className={style.customGridActions}>
                                    <button className={style.customGridActionBtn} title="Edit" onClick={handleEdit}>
                                        <CustomIcon icon="edit" />
                                    </button>
                                    {!file.isDir && (
                                        <button className={style.customGridActionBtn} title="Download" onClick={handleDownload}>
                                            <CustomIcon icon="download" />
                                        </button>
                                    )}
                                    <button className={style.customGridActionBtn} title="Delete" onClick={handleDelete}>
                                        <CustomIcon icon="trash" />
                                    </button>
                                </div>
                                <div className={style.customGridIcon}><FileIcon /></div>
                                <div className={style.customGridName}>{file.name}</div>
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={e => handleCheckboxChange(file, e.target.checked)}
                                    onClick={e => e.stopPropagation()}
                                    className={style.gridCheckbox}
                                />
                            </div>
                        );
                    })}
                </div>
            );
        }
    };

    const handleFileAction = useCallback((data) => {
        switch (data.id) {
            case ChonkyActions.EnableListView.id:
                setIsListView(true);
                break;
            case ChonkyActions.EnableGridView.id:
                setIsListView(false);  
                break;
            case ChonkyActions.DeleteFiles.id: {
                const confirmed = window.confirm(
                    'Are you sure you want to delete the selected items? This action cannot be undone.'
                );
                if (!confirmed) return;
                const selectedFileIds = Array.from(fileBrowserRef.current.getFileSelection());
                const selectedFiles = selectedFileIds
                    .map(id => fileMapRef.current[id])
                    .filter(Boolean); 
    
                const filesToDelete = selectedFiles.filter(file => file.id.startsWith('file_'));
                const foldersToDelete = selectedFiles.filter(file => file.id.startsWith('node_'));
    
                const fileIds = filesToDelete.map(file => file.id.replace('file_', ''));
                const folderIds = foldersToDelete.map(folder => folder.id.replace('node_', ''));
    
                if (fileIds.length + folderIds.length === 0) return;
    
                const session = getSession();
                const headers = {
                    headers: {
                        'X-Auth-Session': session.session,
                        'X-Spark-API-version': commitHash,
                    }
                };
    
                const deletePromises = [
                    ...fileIds.map(id => axios.delete(`${broncoURL}/api/file/${id}`, headers)),
                    ...folderIds.map(id => axios.delete(`${broncoURL}/api/folder/${id}`, headers))
                ];
    
                props.app.setState({ loading: true });
    
                Promise.all(deletePromises)
                    .then(() => refreshFileBrowserData())
                    .catch(error => {
                        console.error('Delete error:', {
                            error: error.message,
                            response: error.response?.data
                        });
                        const message = error.response?.data?.message || error.message;
                        alert(`Deletion failed: ${message}`);
                    })
                    .finally(() => props.app.setState({ loading: false }));
                break;
            }
            case ChonkyActions.OpenFiles.id: {
                const { targetFile, files } = data.payload;
                const fileToOpen = targetFile ?? files[0];
                if (fileToOpen && FileHelper.isDirectory(fileToOpen)) {
                    setSearchQuery('');
                    setCurrentFolderId(fileToOpen.id);
                } 
                break;
            }
            case ChonkyActions.EndDragNDrop.id: {
                const { draggedFile, destination, selectedFiles } = data.payload;
    
                const filesToMove = selectedFiles.length > 0 ? selectedFiles : [draggedFile];
                if (!filesToMove.length || !destination || !destination.isDir) {
                    alert('Invalid move operation');
                    return;
                }
    
                const sourceFileIds = filesToMove.map(file => 
                    file.id.startsWith('file_') ? file.id.slice(5) : file.id
                );
                const destFolderId = destination.id.startsWith('node_') 
                    ? destination.id.slice(5) 
                    : destination.id;
    
                const session = getSession();
    
                props.app.setState({ loading: true });
                axios.post(`${broncoURL}/api/file/move`, {
                    source_file_ids: sourceFileIds,
                    dest_folder_id: destFolderId
                }, {
                    headers: {
                        'X-Auth-Session': session.session,
                        'X-Spark-API-version': commitHash
                    }
                })
                .then(response => {
                    if (response.status === 200) {
                        refreshFileBrowserData();
                    } else {
                        throw new Error('Failed to move files');
                    }
                })
                .catch(error => {
                    console.error('Move error:', error);
                    const message = error.response?.data?.message || error.message;
                    alert(`File move failed: ${message}`);
                })
                .finally(() => props.app.setState({ loading: false }));
                break;
            }
            case 'custom_folder_upload': {
                openCreateFolderModal();
                break;
            }
            case 'custom_edit': {
                openEditModal();
                break;
            }
            case 'custom_file_upload': {
                openCreateFileModal();
                break;
            }
            case 'custom_file_download': {
                handleDownloadFile();
                break
            }
            default:
                break;
        }
    }, [refreshFileBrowserData]);

    const CustomDeleteAction = defineFileAction({
        id: ChonkyActions.DeleteFiles.id,
        button: {
            name: 'Delete', 
            toolbar: true,
            contextMenu: true,
            icon: ChonkyIconName.trash
        },
    });

    const CustomFolderUploadAction = defineFileAction({
        id: 'custom_folder_upload',
        button: {
            name: 'New folder', 
            toolbar: true,
            contextMenu: true,
            icon: ChonkyIconName.folderCreate
        },
    });

    const CustomFileDownload = defineFileAction({
        id: 'custom_file_download',
        button: {
            name: 'Download File', 
            toolbar: true,
            contextMenu: true,
            icon: ChonkyIconName.download,
        },
    });

    const CustomFileUploadAction = defineFileAction({
        id: 'custom_file_upload',
        button: {
            name: 'Upload file', 
            toolbar: true,
            contextMenu: true,
            icon: ChonkyIconName.file
        },
    });

    const CustomEditAction = defineFileAction({
        id: 'custom_edit',
        button: {
            name: 'Edit', 
            toolbar: true,
            contextMenu: true,
            icon: ChonkyIconName.config
        },
    });

    function getSearchResultsLength() {
        let folders = searchResults?.folders || [];
        let files = searchResults?.files || [];

        return folders.length + files.length;
    }

    function formatBytes(bytes) {
        if (bytes === 0 || bytes === undefined || bytes === null) return '-';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    let ChonkyToolbarActions = [
        currentFolderId !== 'root' && CustomFileUploadAction,
        currentFolderId !== 'root' && CustomFileDownload,
        CustomDeleteAction,
        CustomFolderUploadAction,
        ChonkyActions.EnableListView,
        ChonkyActions.EnableGridView,
    ];
    

    const searchComponent = (
        <div className={style.searchContainer}>
            <div className={style.searchInnerContainer}>
                <button 
                    onClick={() => setSearchQuery('')}
                    className={'button'}
                    aria-label="Clear search"
                    >
                    <CustomIcon icon='times' /> 
                </button>
                <input
                    type="text"
                    placeholder="Search all files and folders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={style.searchInput}
                />
            </div>
            {isSearching && getSearchResultsLength() > 0 && (
                <div className={style.searchStatus}>
                    Found {getSearchResultsLength()} result{getSearchResultsLength() !== 1 ? 's' : ''}
                </div>
            )}
        </div>
    );

    return (
        <>   
            <div 
                ref={wrapperRef}
                tabIndex={-1}
                className={style.fileBrowserWrapper}
            >
                {searchComponent}
                <FileBrowser
                    ref={fileBrowserRef}
                    files={filesSortedByNames}
                    folderChain={folderChain}
                    onFileAction={handleFileAction}
                    fileActions={ChonkyToolbarActions}
                    disableDefaultFileActions={true}
                >
                    <FileNavbar />
                    <FileToolbar />
                    <div className={style.customFileListContainer}>
                        <CustomFileList 
                            files={filesSortedByNames} 
                            onFileSelected={(file, isDoubleClick) => {
                            const newSelection = new Set([file.id]);
                            fileBrowserRef.current.setFileSelection(newSelection);
                            
                            if (isDoubleClick && file.isDir) {
                                setCurrentFolderId(file.id);
                            }
                            }}
                            selectedFileIds={selectedFileIds}
                        />
                    </div>
                </FileBrowser>
            </div>
        </>
    );
}

export default ChonkyFileBrowser;